<template>
  <v-dialog :value="dialog" width="1000px" class="d-flex" persistent>
    <v-card class="dialog-content pa-4" height="650">
      <v-card-title>{{ dialogTitle }}</v-card-title>
      <v-row>
        <v-col cols="12">
          <v-row class="pa-2 ma-0">
            <v-col cols="5" class="pa-0">
              <v-row>
                <v-row>
                  <v-container class="pa-0 preview-col ml-8 d-flex align-center justify-center" fill-height>
                    <v-row class="d-flex align-center justify-center">
                      <v-img v-if="prize.image_url && !prizeFile.isLoading" max-height="300" max-width="300" :src="prize.image_url"></v-img>
                      <v-progress-circular v-else-if="prizeFile.isLoading" indeterminate />
                      <span v-else>Aguardando arquivo imagem</span>
                    </v-row>
                  </v-container>
                </v-row>
                <v-row>
                  <v-col>
                    <p v-if="imageError" class="error--text error-text ml-8 pl-0 mb-0">{{ imageError }}</p>
                    <v-container class="import-col ml-8 pl-0" @click="activateInput">
                      <mf-icon fill color="marca-0-l40">cloud_upload</mf-icon>
                      <span class="ml-2 text import">{{ fileName || importLabel }}</span>
                      <p class="text import-hint mb-1">São aceitos os formatos: PNG, JPG, ou JPEG. O arquivo deve ter no máximo 1MB</p>
                      <input id="dynamic-file-input" ref="file-input" class="hidden-input" type="file" @input="readFile($event, 'prizeFile')" />
                    </v-container>
                  </v-col>
                </v-row>
              </v-row>
            </v-col>
            <v-col cols="7" class="pa-4">
              <v-row>
                <v-col cols="8" class="pa-0 pr-2">
                  <v-text-field v-model="prize.title" class="text-field" label="Nome do prêmio" dense outlined counter="70" :error-messages="titleError" />
                </v-col>
                <v-col cols="4" class="pa-0 pl-1">
                  <v-text-field
                    v-model="prize.quantity"
                    class="text-field"
                    label="Quantidade"
                    outlined
                    dense
                    type="number"
                    :error-messages="quantityError"
                    @keydown="preventDefault($event, 'keydown')"
                  />
                </v-col>
              </v-row>
              <v-row class="pa-0">
                <v-col cols="6" class="pa-0 pr-2">
                  <v-tooltip top max-width="300px">
                    <template v-slot:activator="{ on }">
                      <div v-on="on">
                        <mf-date-range-picker
                          label="Data de eligibilidade de vendas"
                          :initial-date.sync="prize.eligibility_start_date"
                          :final-date.sync="prize.eligibility_end_date"
                          outlined
                          icon
                          dense
                          :min="currentDate"
                          :error-messages="eligibilityDateError"
                          @input="prize.raffle_date = ''"
                        />
                      </div>
                    </template>
                    <span
                      >Período em que as compras realizadas são elegíveis para a participação da campanha de sorteio. Compras realizadas fora desse período não
                      geraram números da sorte para esse prêmio.</span
                    >
                  </v-tooltip>
                </v-col>
                <v-col cols="6" class="pa-0 pl-2">
                  <date-picker
                    class="date-picker"
                    :date.sync="prize.raffle_date"
                    :min="minDate"
                    outlined
                    dense
                    label="Data do sorteio"
                    :error-messages="raffleDateError"
                  />
                </v-col>
              </v-row>
              <v-row class="d-flex justify-center align-center">
                <v-col cols="1" class="pa-0 ma-0">
                  <mf-icon fill color="vermelho-0" class="mr-2" size="25">warning</mf-icon>
                </v-col>
                <v-col class="pa-0 ma-0">
                  <p class="ma-0">A data final do "Período de compras elegíveis" deve ser pelo menos 3 dias antes da "Data do sorteio".</p>
                </v-col>
              </v-row>
            </v-col>
          </v-row>
        </v-col>
      </v-row>
      <mf-action-buttons
        class="pb-4 pr-4"
        :primary-button="{
          text: 'Salvar',
          action: confirm,
          isVisible: true,
        }"
        :extra-button="{
          text: 'Cancelar',
          action: cancel,
          isVisible: true,
        }"
      />
    </v-card>
  </v-dialog>
</template>

<script>
import moment from 'moment'
import { minLength, maxLength, required, minValue } from 'vuelidate/lib/validators'
import { notOnlySpace, isFuture } from '@/helpers/validators.js'
import { preventNumberEvents } from '../../helpers'
import DatePicker from '@/components/DatePicker.vue'

export default {
  name: 'PrizeDialog',
  components: {
    DatePicker,
  },
  props: {
    save: {
      required: true,
      type: Function,
    },
    prize: {
      type: Object,
      required: true,
    },
    readFile: {
      required: true,
      type: Function,
    },
    prizeFile: {
      type: Object,
      required: true,
    },
    raffleStartDate: {
      type: String,
      required: false,
    },
  },
  data: () => ({
    dialog: true,
    fileName: '',
    importLabel: 'Importar imagem',
    fileTypes: ['image/png', 'image/jpg', 'image/jpeg', ''],
  }),
  computed: {
    dialogTitle() {
      return this.prize.isEditing ? 'Editar prêmio' : 'Criar prêmio'
    },
    minDate() {
      return this.prize.eligibility_end_date ? moment(this.prize.eligibility_end_date).add(3, 'days').toISOString() : moment().add(3, 'days').toISOString()
    },
    quantityError() {
      if (!this.$v.prize.quantity) return ''
      if (this.$v.prize.quantity.$error) {
        if (!this.$v.prize.quantity.required) return 'Indique a quantidade do prêmio.'
        else if (!this.$v.prize.quantity.minValue) return 'Indique a quantidade do prêmio.'
      }
      return ''
    },
    eligibilityDateError() {
      if (!this.$v.prize.eligibility_start_date) return ''
      if (this.$v.prize.eligibility_start_date.$error) {
        if (!this.$v.prize.eligibility_start_date.required) return 'Selecione um período de eligibilidade para o prêmio.'
      }

      if (!this.$v.prize.eligibility_end_date) return ''
      if (this.$v.prize.eligibility_end_date.$error) {
        if (!this.$v.prize.eligibility_end_date.required) return 'Selecione um período de eligibilidade para o prêmio.'
      }
      return ''
    },
    raffleDateError() {
      if (!this.$v.prize.raffle_date) return ''
      if (this.$v.prize.raffle_date.$error) {
        if (!this.$v.prize.raffle_date.required) return 'Selecione uma data para o sorteio.'
        else if (!this.$v.prize.raffle_date.isFuture) return 'A data de sorteio deve ser maior que a atual.'
      }
      return ''
    },
    titleError() {
      if (!this.$v.prize.title) return ''
      if (this.$v.prize.title.$error) {
        if (!this.$v.prize.title.required) return 'Dê um nome para este prêmio.'
        else if (!this.$v.prize.title.minLength) return 'O título possui menos de 2 caracteres.'
        else if (!this.$v.prize.title.maxLength) return 'O título excedeu 70 caracteres.'
        else if (!this.$v.prize.title.notOnlySpace) return 'Não é permitido salvar apenas espaços.'
      }
      return ''
    },
    imageError() {
      if (!this.$v.prize.image_url) return ''
      if (this.$v.prize.image_url.$error) {
        if (!this.$v.prize.image_url.required) return 'Adicione uma imagem do prêmio.'
      }
      return ''
    },
    currentDate() {
      return moment().format('YYYY-MM-DD')
    },
  },
  methods: {
    preventDefault(event, type) {
      return preventNumberEvents(event, type)
    },
    confirm() {
      if (this.validate()) {
        this.save()
        this.$emit('input', false)
      }
    },
    validate() {
      this.$v.$reset()
      this.$v.$touch()
      if (this.$v.$error) {
        this.$snackbar({ message: 'Preencha todos os campos corretamente', snackbarColor: 'error' })
        return false
      }
      return true
    },
    cancel() {
      this.$emit('input', false)
    },
    activateInput() {
      this.$refs['file-input'].click()
      document.querySelector('#dynamic-file-input').value = '' // This enables secting the same file again
    },
  },
  validations() {
    let validations = {
      prize: {
        title: {
          required,
          minLength: minLength(2),
          maxLength: maxLength(70),
          notOnlySpace,
        },
        image_url: {
          required,
          notOnlySpace,
        },
        raffle_date: {
          required,
          isFuture,
        },
        eligibility_start_date: {
          required,
        },
        eligibility_end_date: {
          required,
        },
        quantity: {
          required,
          minValue: minValue(1),
        },
      },
    }

    return validations
  },
}
</script>

<style lang="scss" scoped>
.dialog-content {
  .block-title {
    opacity: 0.8;
    font-size: 18px;
    font-weight: bold;
    color: $preto;
  }
}
.file-preview {
  background-color: $cinza-l30;
}
.hidden-input {
  display: none;
}
.text {
  opacity: 0.87;
  max-width: 300px;
  &.import {
    color: $marca-0-l40;
  }
  &.import-hint {
    font-size: 12px;
    opacity: 0.54;
  }
}
.error--text {
  font-size: 12px;
}
.category-box {
  .select-label {
    opacity: 0.87;
  }
  .disabled-opacity {
    opacity: 0.54;
  }
}
.preview-col {
  height: 350px;
  width: 350px;
  background-color: $cinza-l90;
  border-radius: 4px;
  border: 1px solid $cinza-l30;
  padding: 2px;
}
.preview-row {
  overflow: auto;
  max-height: 450px;
  max-width: 600px;
}
.import-col {
  cursor: pointer;
  user-select: none;
}
::v-deep input::-webkit-outer-spin-button,
::v-deep input::-webkit-inner-spin-button {
  -webkit-appearance: none;
  margin: 0;
}
</style>
